export interface DirectoryImageBadge {
  href: string;
  src: string;
  /** Dark-theme image in the same anchor (InstantLaunch Verify / Vemoir-style). */
  srcDark?: string;
  alt: string;
  /** Anchor aria-label when the link wraps multiple images. */
  linkAriaLabel?: string;
  width?: number;
  height?: number;
  target?: '_blank';
  rel?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  imgStyle?: string;
  textLabel?: never;
}

export interface DirectoryTextBadge {
  href: string;
  textLabel: string;
  target?: '_blank';
  rel?: string;
  src?: never;
  srcDark?: never;
  alt?: never;
}

export type DirectoryBadge = DirectoryImageBadge | DirectoryTextBadge;

export function isDirectoryTextBadge(
  badge: DirectoryBadge
): badge is DirectoryTextBadge {
  return 'textLabel' in badge && badge.textLabel != null;
}

/** Shared img classes for dual-theme directory badges (InstantLaunch verify). */
export const DIRECTORY_BADGE_IMG_BASE_CLASS = 'footer-badge-img';
export const DIRECTORY_BADGE_IMG_THEME_LIGHT_CLASS =
  'footer-badge-img footer-badge-img--theme-light';
export const DIRECTORY_BADGE_IMG_THEME_DARK_CLASS =
  'footer-badge-img footer-badge-img--theme-dark';

/** Same-origin copies of directory artwork. href/rel stay on the listing. */
const DIRECTORY_BADGE_ASSET = '/directory-badges';

/** Plain HTML directory badges (no JS widgets). Keep in sync with CitySage developer badge brief. */
export const DIRECTORY_BADGES: DirectoryBadge[] = [
  {
    href: 'https://neeed.directory/products/citysage?utm_source=citysage',
    src: `${DIRECTORY_BADGE_ASSET}/neeed-badge-light.svg`,
    alt: 'Featured on neeed.directory',
    width: 139,
    height: 20,
    target: '_blank',
    rel: 'noopener',
  },
  {
    href: 'https://wired.business',
    src: `${DIRECTORY_BADGE_ASSET}/wired-business-badge.svg`,
    alt: 'Featured on Wired Business',
    width: 200,
    height: 54,
    target: '_blank',
  },
  {
    href: 'https://fazier.com',
    src: `${DIRECTORY_BADGE_ASSET}/fazier-launch-light.svg`,
    alt: 'Fazier badge',
    width: 120,
    target: '_blank',
  },
  {
    href: 'https://thesaasdir.com/product/citysage?ref=badge',
    src: `${DIRECTORY_BADGE_ASSET}/thesaasdir-citysage.svg`,
    alt: 'Featured on TheSaaSDir',
    width: 182,
    height: 46,
    rel: 'dofollow',
  },
  {
    href: 'https://nicklaunches.com/products/citysage/?utm_source=citysage.ch&utm_medium=badge&utm_campaign=featured',
    src: `${DIRECTORY_BADGE_ASSET}/nicklaunches-featured.png`,
    alt: 'CitySage on Nick Launches',
    width: 244,
    height: 56,
    target: '_blank',
    rel: 'noopener',
  },
  {
    href: 'https://www.instantlaunch.xyz/citysage',
    src: `${DIRECTORY_BADGE_ASSET}/instantlaunch-badge-light.svg`,
    srcDark: `${DIRECTORY_BADGE_ASSET}/instantlaunch-badge-dark.svg`,
    alt: 'Verified on InstantLaunch',
    linkAriaLabel: 'Verified on InstantLaunch',
    width: 200,
    height: 54,
    target: '_blank',
    rel: 'noopener noreferrer',
  },
  {
    href: 'https://twelve.tools',
    src: `${DIRECTORY_BADGE_ASSET}/twelve-tools-badge.svg`,
    alt: 'Featured on Twelve Tools',
    width: 200,
    height: 54,
    target: '_blank',
  },
  {
    href: 'https://saasbison.com',
    src: `${DIRECTORY_BADGE_ASSET}/saasbison-badge.png`,
    alt: 'Featured on SaaSBison',
    width: 200,
    height: 54,
    target: '_blank',
    rel: 'dofollow',
  },
  {
    href: 'https://dailypings.com/p/citysage',
    src: `${DIRECTORY_BADGE_ASSET}/dailypings-badge.svg`,
    alt: 'Featured on DailyPings',
    width: 179,
    height: 32,
    target: '_blank',
    rel: 'noopener',
  },
  {
    href: 'https://sumodir.com/',
    textLabel: 'Featured on SumoDir',
    target: '_blank',
    rel: 'dofollow',
  },
  {
    href: 'https://dododirectory.com/',
    textLabel: 'Featured on DodoDirectory',
    target: '_blank',
    rel: 'dofollow',
  },
  {
    href: 'https://www.toolpilot.ai/',
    textLabel: 'Featured on ToolPilot',
    target: '_blank',
    rel: 'dofollow',
  },
];

const DEFAULT_BADGE_IMG_STYLE = 'max-height:54px;width:auto';

export const CITYSAGE_STATIC_DIRECTORY_BADGES_ID =
  'citysage-static-directory-badges';

const VISIBLE_SHELL_STYLE =
  'max-width:72rem;margin:2rem auto 0;padding:1.5rem 1rem;border-top:1px solid #f3f4f6;background:#fff';
const VISIBLE_HEADING_STYLE =
  'font-size:0.875rem;font-weight:600;color:#4b5563;text-align:center;margin:0 0 1rem';
const VISIBLE_ROW_STYLE =
  'display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:1.5rem';

/** Keep in sync with apps/web/src/directory-badge-theme.css (tailwind darkMode: class → `.dark` on html). */
const DIRECTORY_BADGE_THEME_STYLE_BLOCK = `<style>
.footer-badge-img { max-height: 54px; width: auto; object-fit: contain; }
.footer-badge-img--theme-dark,
.dark .footer-badge-img--theme-light { display: none; }
.dark .footer-badge-img--theme-dark { display: block; }
</style>`;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type DirectoryBadgeImgTheme = 'light' | 'dark' | 'single';

/**
 * Visible SEO shell badge row (Oblifinder-style) in initial HTML for crawlers.
 */
function buildDirectoryBadgeImgHtml(
  badge: DirectoryImageBadge,
  src: string,
  theme: DirectoryBadgeImgTheme
): string {
  const imgClass =
    theme === 'light'
      ? DIRECTORY_BADGE_IMG_THEME_LIGHT_CLASS
      : theme === 'dark'
      ? DIRECTORY_BADGE_IMG_THEME_DARK_CLASS
      : DIRECTORY_BADGE_IMG_BASE_CLASS;

  const imgStyle = badge.imgStyle ?? DEFAULT_BADGE_IMG_STYLE;
  const imgAttrs = [
    `class="${imgClass}"`,
    `src="${escapeHtml(src)}"`,
    `alt="${escapeHtml(badge.alt)}"`,
    badge.width != null ? `width="${badge.width}"` : '',
    badge.height != null ? `height="${badge.height}"` : '',
    badge.loading ? `loading="${badge.loading}"` : '',
    badge.decoding ? `decoding="${badge.decoding}"` : '',
    `style="${imgStyle}"`,
  ]
    .filter(Boolean)
    .join(' ');

  return `<img ${imgAttrs} />`;
}

const VISIBLE_TEXT_LINK_STYLE =
  'font-size:0.875rem;color:#4b5563;text-decoration:underline';

export function buildDirectoryBadgesHtml(ariaLabel = 'As seen on'): string {
  const links = DIRECTORY_BADGES.map((badge) => {
    const attrs = [
      badge.target ? `target="${badge.target}"` : '',
      badge.rel ? `rel="${escapeHtml(badge.rel)}"` : '',
      !isDirectoryTextBadge(badge) && badge.linkAriaLabel
        ? `aria-label="${escapeHtml(badge.linkAriaLabel)}"`
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    if (isDirectoryTextBadge(badge)) {
      return `        <a href="${escapeHtml(badge.href)}"${
        attrs ? ` ${attrs}` : ''
      } style="${VISIBLE_TEXT_LINK_STYLE}">${escapeHtml(badge.textLabel)}</a>`;
    }

    const imgs = badge.srcDark
      ? `${buildDirectoryBadgeImgHtml(
          badge,
          badge.src,
          'light'
        )}${buildDirectoryBadgeImgHtml(badge, badge.srcDark, 'dark')}`
      : buildDirectoryBadgeImgHtml(badge, badge.src, 'single');

    return `        <a href="${escapeHtml(badge.href)}"${
      attrs ? ` ${attrs}` : ''
    }>${imgs}</a>`;
  }).join('\n');

  return `<section id="${CITYSAGE_STATIC_DIRECTORY_BADGES_ID}" aria-label="${escapeHtml(
    ariaLabel
  )}" data-citysage-directory-badges>
      ${DIRECTORY_BADGE_THEME_STYLE_BLOCK}
      <div style="${VISIBLE_SHELL_STYLE}">
        <p style="${VISIBLE_HEADING_STYLE}">${escapeHtml(ariaLabel)}</p>
        <div style="${VISIBLE_ROW_STYLE}">
${links}
        </div>
      </div>
    </section>`;
}

export const DIRECTORY_BADGES_HTML_MARKER = 'https://dailypings.com/p/citysage';
